import ReactDOM from 'react-dom/client';
import { Amplify } from 'aws-amplify';
// libs
import { BrowserRouter } from 'react-router-dom';
// material-ui
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LicenseInfo } from '@mui/x-license-pro';
// react-query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
// components
import App from 'App'
import { runtimeConfig } from 'constant/runtime';
import AuthProvider from 'context/Auth/AuthProvider';
import GlobalStateProvider from 'context/GlobalState/GlobalStateProvider';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
// styles
import theme from 'themes';

import 'style.css';
import PartTypesAuthProvider from 'context/PartTypes/AuthProvider';
import SearchAuthProvider from 'context/Search/AuthProvider';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';

dayjs.extend(relativeTime);

const authConfig: Record<string, unknown> = {
  region: runtimeConfig.REACT_APP_AWS_COGNITO_REGION,
  userPoolId: runtimeConfig.REACT_APP_AWS_USER_POOLS_ID,
  userPoolWebClientId: runtimeConfig.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID,
};

// Enable Cognito Hosted UI (Google SSO) only when a domain is configured.
// Without it the app keeps its existing email/password-only behaviour.
if (runtimeConfig.REACT_APP_AWS_COGNITO_DOMAIN) {
  const redirectDefault = `${window.location.origin}/`;
  authConfig.oauth = {
    domain: runtimeConfig.REACT_APP_AWS_COGNITO_DOMAIN,
    scope: ['email', 'openid', 'profile'],
    redirectSignIn: runtimeConfig.REACT_APP_OAUTH_REDIRECT_SIGN_IN || redirectDefault,
    redirectSignOut: runtimeConfig.REACT_APP_OAUTH_REDIRECT_SIGN_OUT || redirectDefault,
    responseType: 'code',
  };
}

Amplify.configure({ Auth: authConfig });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 5 * 1000,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

LicenseInfo.setLicenseKey(runtimeConfig.REACT_APP_MUI_PRO_X_LICENSE_KEY || '');

// const INTERCOM_APP_ID: string | undefined = process.env.REACT_APP_INTERCOM_APP_ID;

root.render(
  <BrowserRouter>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <GlobalStateProvider>
          <AuthProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <PartTypesAuthProvider>
                <SearchAuthProvider>
                  <DndProvider backend={HTML5Backend}>
                    <App />
                  </DndProvider>
                </SearchAuthProvider>
              </PartTypesAuthProvider>
            </LocalizationProvider>
          </AuthProvider>
        </GlobalStateProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </BrowserRouter>
);
